import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash, FaInfoCircle } from "react-icons/fa";
import toast from "react-hot-toast";
import api from "services/api";

export const Register = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Auth
    email: "",
    password: "",
    confirm_password: "",
    phone_number: "",

    // Basic information
    first_name: "",
    last_name: "",
    date_of_birth: "",
    location: "",

    // Metrics
    height: "",
    sex: "",
    current_weight: "",
    target_weight: "",
    body_fat: "",

    // Goals and Progress
    goal: "",
    rate_of_progress: "",
    activity_level: "",
  });

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/auth/register", formData);
      if (!response.ok) {
        console.log(response);
        // toast.error(response.message);
        response.message.forEach((message) => {
          toast.error(message);
        });
        return;
      }

      toast.success(response.message || "Registration successful!");
      await new Promise((resolve) => setTimeout(resolve, 3000));
      navigate("/login");
    } catch (error) {
      toast.error(error.message || "An error occurred during registration");
    }
  };

  const steps = [
    {
      component: BasicInfo,
      title: "Personal information",
      validate: BasicInfo.validate,
    },
    {
      component: Metrics,
      title: "Physical metrics",
      validate: Metrics.validate,
    },
    { component: Goal, title: "Fitness goal", validate: Goal.validate },
    {
      component: ActivityLevel,
      title: "Activity level",
      validate: ActivityLevel.validate,
    },
    {
      component: RateOfProgress,
      title: "Rate of progress",
      validate: RateOfProgress.validate,
    },
    { component: Auth, 
      title: "Account details", 
      validate: Auth.validate 
    },
  ];

  const CurrentStepComponent = steps[currentStep - 1].component;
  const validateCurrentStep = steps[currentStep - 1].validate;

  const handleNext = async (e) => {
    e.preventDefault();
    const error = validateCurrentStep(formData);
    if (error) {
      toast.error(error);
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        handleSubmit(e);
      } catch (error) {
        toast.error(error.message || "An error occurred during registration");
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-extrabold text-black">
          Create your account
        </h1>
        <div className="mt-4 text-center text-sm text-black">
          <p className="font-bold">Step {currentStep} of {steps.length}</p>
          <p className="mt-1">Don't worry, it will only take a few minutes.</p>
          <p className="mt-1">All fields with a * are required.</p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <CurrentStepComponent
              formData={formData}
              handleChange={handleChange}
            />

            <div className="flex justify-between mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex justify-center py-2 px-4 border border-black rounded-md shadow-sm text-sm font-medium text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                >
                  Previous
                </button>
              )}

              <button
                type="button"
                onClick={handleNext}
                className="ml-auto inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
              >
                {currentStep === steps.length ? "Create account" : "Next"}
              </button>
            </div>
          </form>
        </div>
        <div className="mt-2 text-center text-sm text-gray-600">
          <p>
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-black hover:text-gray-700">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const BasicInfo = ({ formData, handleChange }) => {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Personal information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="first_name"
          >
            First name *
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="last_name">
            Last name
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="date_of_birth"
          >
            Date of birth *
          </label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={(e) => handleChange("date_of_birth", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            required
          />
        </div>

        {/* <div>
          <label className="block text-sm font-medium mb-1" htmlFor="location">
            Postal code
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={(e) => handleChange("location", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="75001"
          />
        </div> */}
      </div>
    </div>
  );
};

BasicInfo.validate = (formData) => {
  if (!formData.first_name) return "First name is required";
  if (formData.first_name.length < 2) return "First name must contain at least 2 characters";
  if (!formData.date_of_birth) return "Date of birth is required";
  if (formData.location && formData.location.length !== 5) return "Postal code must contain 5 characters";

  const today = new Date();
  const dob = new Date(formData.date_of_birth);
  if (dob > today) return "Date of birth cannot be in the future";
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  if (age < 18) return "You must be at least 18 years old";

  return null;
};

const Metrics = ({ formData, handleChange }) => {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Physical metrics</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="height">
            Height (cm) *
          </label>
          <input
            type="number"
            id="height"
            name="height"
            value={formData.height}
            onChange={(e) => handleChange("height", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            required
            min="0"
          />
        </div>
        <div>
          <label
            className="block text-sm font-medium mb-1 flex items-center gap-1"
            htmlFor="sex"
          >
            Sex *
            <div className="group relative">
            </div>
          </label>

          <div className="grid grid-cols-2 gap-4">
            {[
              {
                id: "male",
                label: "Male",
              },
              {
                id: "female",
                label: "Female",
              },
            ].map((sex) => (
              <div
                key={sex.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  formData.sex === sex.id
                    ? "border-black bg-gray-50"
                    : "border-gray-200 hover:border-gray-400"
                }`}
                onClick={() => handleChange("sex", sex.id)}
              >
                <h3 className="font-medium text-black">{sex.label}</h3>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="current_weight"
          >
            Current weight (kg) *
          </label>
          <input
            type="number"
            id="current_weight"
            name="current_weight"
            value={formData.current_weight}
            onChange={(e) => handleChange("current_weight", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            required
            min="30"
            step="0.1"
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="target_weight"
          >
            Target weight (kg) *
          </label>
          <input
            type="number"
            id="target_weight"
            name="target_weight"
            value={formData.target_weight}
            onChange={(e) => handleChange("target_weight", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            required
            min="0"
            step="0.1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="body_fat">
            Body fat percentage (optional)
          </label>
          <input
            type="number"
            id="body_fat"
            name="body_fat"
            value={formData.body_fat}
            onChange={(e) => handleChange("body_fat", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            min="0"
            max="100"
            step="0.1"
          />
        </div>
      </div>
    </div>
  );
};

Metrics.validate = (formData) => {
  if (!formData.height) return "Height is required";
  if (!formData.current_weight) return "Current weight is required";
  if (!formData.target_weight) return "Target weight is required";
  if (!formData.sex) return "Sex is required";

  if (
    formData.height <= 0 ||
    formData.current_weight <= 0 ||
    formData.target_weight <= 0
  ) {
    return "Height and weight must be greater than 0";
  }

  if (formData.body_fat && (formData.body_fat < 0 || formData.body_fat > 100)) {
    return "Body fat percentage must be between 0 and 100";
  }

  return null;
};

const Goal = ({ formData, handleChange }) => {
  const goals = [
    {
      value: "lose_fat",
      label: "Lose fat",
      description:
        "Focus on reducing body fat while maintaining muscle mass through a caloric deficit and appropriate training.",
    },
    {
      value: "gain_muscle", 
      label: "Gain muscle",
      description:
        "Increase muscle mass and strength through progressive overload and adequate protein intake.",
    },
    {
      value: "maintain_weight",
      label: "Maintain weight", 
      description:
        "Maintain current body composition while improving overall fitness and performance.",
    },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Fitness goal</h2>

      <div className="grid grid-cols-1 gap-4">
        {goals.map((goal) => (
          <div
            key={goal.value}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              formData.goal === goal.value
                ? "border-black bg-gray-50"
                : "border-gray-200 hover:border-gray-400"
            }`}
            onClick={() => handleChange("goal", goal.value)}
          >
            <h3 className="font-medium text-black">{goal.label}</h3>
            <p className="text-sm text-black mt-1">{goal.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

Goal.validate = (formData) => {
  if (!formData.goal) return "Please select a fitness goal";
  if (formData.goal === "lose_fat" && formData.target_weight >= formData.current_weight)
    return "Target weight must be less than current weight if you want to lose fat";
  if (formData.goal === "gain_muscle" && formData.target_weight <= formData.current_weight)
    return "Target weight must be greater than current weight if you want to gain muscle";
  if (formData.goal === "maintain_weight" && formData.target_weight !== formData.current_weight)
    return "Target weight must be equal to current weight if you want to maintain your weight";
  return null;
};

const ActivityLevel = ({ formData, handleChange }) => {
  const activityLevels = [
    {
      value: "sedentary",
      label: "Sedentary",
      description:
        "Little or no exercise, desk job or sitting most of the day.",
    },
    {
      value: "light",
      label: "Light",
      description:
        "Light exercise 1-3 times per week, mainly walking or standing work.",
    },
    {
      value: "moderate",
      label: "Moderate",
      description:
        "Moderate exercise 3-5 times per week, mix of cardio and strength training.",
    },
    {
      value: "active",
      label: "Active",
      description:
        "Intense exercise 4-6 times per week, intense training sessions.",
    },
    {
      value: "very_active",
      label: "Very active",
      description:
        "Professional athlete level, training multiple times per day or physical labor.",
    },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Activity level</h2>

      <div className="grid grid-cols-1 gap-4">
        {activityLevels.map((level) => (
          <div
            key={level.value}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              formData.activity_level === level.value
                ? "border-black bg-gray-50"
                : "border-gray-200 hover:border-gray-400"
            }`}
            onClick={() => handleChange("activity_level", level.value)}
          >
            <h3 className="font-medium text-black">{level.label}</h3>
            <p className="text-sm text-black mt-1">{level.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

ActivityLevel.validate = (formData) => {
  if (!formData.activity_level) return "Please select an activity level";
  return null;
};

const RateOfProgress = ({ formData, handleChange }) => {
  const rates = [
    {
      value: "slow",
      label: "Slow",
      description:
        "Gradual progression with minimal lifestyle changes, suitable for people with busy schedules. (1kg/month)",
    },
    {
      value: "moderate",
      label: "Moderate",
      description:
        "Balanced approach with steady progression and sustainable changes. (2kg/month)",
    },
    {
      value: "fast",
      label: "Fast",
      description:
        "Rapid progression requiring significant commitment and substantial lifestyle modifications. (3kg/month)",
    },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Rate of progress</h2>

      <div className="space-y-4">
        {rates.map((rate) => (
          <div
            key={rate.value}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              formData.rate_of_progress === rate.value
                ? "border-black bg-gray-50"
                : "border-gray-200 hover:border-gray-400"
            }`}
            onClick={() => handleChange("rate_of_progress", rate.value)}
          >
            <h3 className="font-medium text-black">{rate.label}</h3>
            <p className="text-sm text-black mt-1">{rate.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

RateOfProgress.validate = (formData) => {
  if (!formData.rate_of_progress) return "Please select a rate of progress";
  return null;
};

const Auth = ({ formData, handleChange }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Account information</h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="email">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="password">
            Password *
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black pr-10"
              required
              minLength="8"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <FaEyeSlash className="w-5 h-5" />
              ) : (
                <FaEye className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-black mt-1">Minimum 8 characters</p>
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="confirm_password"
          >
            Confirm password *
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirm_password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={(e) => handleChange("confirm_password", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black pr-10 ${
                formData.password !== formData.confirm_password
                  ? "border-black"
                  : ""
              }`}
              required
              minLength="8"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <FaEyeSlash className="w-5 h-5" />
              ) : (
                <FaEye className="w-5 h-5" />
              )}
            </button>
          </div>
          {formData.password && formData.confirm_password && formData.password !== formData.confirm_password && (
            <p className="text-xs text-black mt-1">Passwords do not match</p>
          )}
        </div>

        {/* <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="phone_number"
          >
            Phone number *
          </label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={(e) => handleChange("phone_number", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="+1234567890"
          />
        </div> */}
      </div>
    </div>
  );
};

Auth.validate = (formData) => {
  if (!formData.password) return "Password is required";
  if (formData.password.length < 8) return "Password must contain at least 8 characters";
  if (!formData.confirm_password) return "Password confirmation is required";
  if (formData.password !== formData.confirm_password) return "Passwords do not match";

  return null;
};